// The API client. THE ONLY MODULE IN THIS APP THAT KNOWS HTTP EXISTS.
//
// Nothing under `ui/` or `screens/` may import `fetch`, construct a URL, or read a status code. That
// separation is the reason this file exists as a layer rather than a helper: it is what makes the app
// packageable with Capacitor later (swap the base URL and the credential transport, change nothing
// else) and embeddable in another shell (hand it a different session provider).
//
// ── THE ERROR CONTRACT IS PART OF THE API, NOT A DETAIL ──────────────────────────────────────────
// The backend distinguishes five refusals and the UI must render five different things. Collapsing
// them into `throw new Error(res.statusText)` — the default shape of every fetch wrapper ever written
// — would make a stale-revision conflict indistinguishable from a permission problem.

/** A project the caller may not see answers 404, deliberately indistinguishable from absent. */
export class NotFoundError extends Error {
  constructor(message = 'niet gevonden') { super(message); this.name = 'NotFoundError'; }
}
/** No session, or an expired one. The app must return to the login screen. */
export class UnauthorizedError extends Error {
  constructor(message = 'niet aangemeld') { super(message); this.name = 'UnauthorizedError'; }
}
/** Authenticated, but not allowed. The app must NOT return to login — that would be a loop. */
export class ForbiddenError extends Error {
  constructor(message = 'geen toestemming') { super(message); this.name = 'ForbiddenError'; }
}
/**
 * Somebody else moved this plan first.
 *
 * THE MOST IMPORTANT ERROR IN THIS FILE. A drag that loses this race must snap back and show what
 * happened, never silently keep the optimistic position — see `lib/mutate.ts`.
 */
export class ConflictError extends Error {
  constructor(message = 'iemand anders heeft dit ondertussen gewijzigd') {
    super(message); this.name = 'ConflictError';
  }
}
/** A domain rule refused the write. The message comes from the server and is shown verbatim. */
export class DomainError extends Error {
  constructor(message: string) { super(message); this.name = 'DomainError'; }
}

/** Read the CSRF token the server set. Not HttpOnly precisely so this can read it. */
function csrfToken(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)__Host-bruce_csrf=([^;]+)/);
  return m ? m[1] : null;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Idempotency key for a guarded write. Generated per USER INTENT, not per retry. */
  correlationId?: string;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';

  // CSRF ON STATE-CHANGING METHODS ONLY, matching the server exactly. Sending it on a GET would be
  // harmless and would also mean the two sides no longer agree on when it is required, which is how
  // one of them later stops checking.
  if (method !== 'GET') {
    const t = csrfToken();
    if (t) headers['x-bruce-csrf'] = t;
  }
  // `Idempotency-Key` is the header the server reads, and a malformed one is a 400 rather than a
  // silent fallback — a client that believed it sent a key and got a generated one would replay into a
  // SECOND write. The allowed shape is 8-200 chars of [A-Za-z0-9_.:-]; a UUID satisfies it.
  if (opts.correlationId) headers['idempotency-key'] = opts.correlationId;

  const res = await fetch(path, {
    method,
    headers,
    // THE SESSION IS A COOKIE AND IS NEVER TOUCHED BY THIS CODE. There is no token in localStorage, no
    // Authorization header to forget to clear, and nothing for a logging library to serialise.
    credentials: 'same-origin',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (res.ok) {
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  // The body may not be JSON on an infrastructure error; never let that mask the status.
  let payload: { error?: string; message?: string; issues?: unknown } = {};
  try { payload = await res.json(); } catch { /* keep the status-based answer */ }
  const msg = payload.message ?? payload.error ?? res.statusText;

  switch (res.status) {
    case 401: throw new UnauthorizedError(msg);
    case 403: throw new ForbiddenError(msg);
    case 404: throw new NotFoundError(msg);
    case 409: throw new ConflictError(msg);
    case 400: throw new DomainError(msg);
    case 429: throw new DomainError(msg);
    default: throw new Error(`${res.status}: ${msg}`);
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, correlationId?: string) =>
    request<T>(path, { method: 'POST', body, correlationId }),
  put: <T>(path: string, body?: unknown, correlationId?: string) =>
    request<T>(path, { method: 'PUT', body, correlationId }),
  patch: <T>(path: string, body?: unknown, correlationId?: string) =>
    request<T>(path, { method: 'PATCH', body, correlationId }),
  del: <T>(path: string, correlationId?: string) =>
    request<T>(path, { method: 'DELETE', correlationId }),
};
